import type { DefinitionResponse } from '../generated';
import {
  CustomPropertyInputType,
  CustomPropertyType,
  type CustomPsetInstanceProperty,
  type CustomPsetPropertyValue,
  type CustomPsetScalarValue,
} from './custom-pset.types';

/**
 * @intent Pure schema → domain mapping helpers for custom property sets.
 * Kept dependency-free so unit tests don't need to mock the pset API service.
 *
 * Type classification mirrors tcweb's `@fulcrum/cdm-components`
 * `schemaTypeToPropTypes` so the panel can format values identically.
 */

type PsetSchemaProperty = {
  type?: string;
  format?: string;
  enum?: readonly unknown[];
  required?: boolean;
  items?: PsetSchemaProperty;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- OpenAPI-style schema vendor extension key
  'x-query'?: string;
};

type PsetSchemaWithRequired = DefinitionResponse['schema'] & {
  required?: readonly string[];
};

type DisplayProperties = Record<string, CustomPsetInstanceProperty>;

export function makeDefinitionKey(libId: string, defId: string): string {
  return `${libId}::${defId}`;
}

export function toDisplayProperties(
  definition: DefinitionResponse | undefined,
  props: Record<string, unknown>
): DisplayProperties {
  if (definition === undefined) {
    return Object.fromEntries(
      Object.entries(props).map(([key, value]) => [
        key,
        makeTextProperty(key, value),
      ])
    );
  }

  const result: DisplayProperties = {};
  for (const [id, schemaProp] of Object.entries(definition.schema.props)) {
    const prop = asSchemaProperty(schemaProp);
    const required = isPropertyRequired(definition, id, prop);
    const [propType, inputType] = schemaTypeToPropTypes(prop, required);
    // Deliberately ignore `prop.default`: schema defaults are applied when the
    // pset instance is created, so a value the user has since cleared is absent
    // here and must render as "--" rather than snapping back to the default.
    const instanceProperty: CustomPsetInstanceProperty = {
      id,
      value: toPropertyValue(props[id]),
      propType,
      inputType,
    };

    if (propType === CustomPropertyType.SingleEnumValue) {
      instanceProperty.enumOptions = getEnumOptions(prop);
      instanceProperty.required = required;
    }

    result[getPropertyLabel(definition, id)] = instanceProperty;
  }

  // Fold in props not described by the schema so the UI still shows them.
  for (const [key, value] of Object.entries(props)) {
    if (!(key in definition.schema.props)) {
      result[key] = makeTextProperty(key, value);
    }
  }

  return result;
}

export function getDefinitionName(
  definition: DefinitionResponse | undefined
): string | undefined {
  if (definition === undefined) return undefined;

  const localizedName = getI18n(definition)?.name;
  return typeof localizedName === 'string' ? localizedName : definition.name;
}

/**
 * Ports tcweb's `@fulcrum/cdm-components` `schemaTypeToPropTypes`: maps an
 * OpenAPI-style schema property to a `[structure, dataType]` pair. Returns
 * `[Unknown, Unknown]` for shapes the panel cannot format.
 */
export function schemaTypeToPropTypes(
  prop: PsetSchemaProperty,
  required: boolean
): [CustomPropertyType, CustomPropertyInputType] {
  const propType = schemaTypeToPropType(prop);
  const inputType = schemaTypeToPropInputType(prop, required);

  if (
    propType === CustomPropertyType.Unknown ||
    inputType === CustomPropertyInputType.Unknown
  ) {
    return [CustomPropertyType.Unknown, CustomPropertyInputType.Unknown];
  }

  return [propType, inputType];
}

/**
 * Classifies structural PropType from open OpenAPI `type` strings.
 * `default: break` → Unknown is intentional: these are not closed unions, so
 * UnreachableCaseError does not apply — throwing would crash on unknown schema.
 */
function schemaTypeToPropType(prop: PsetSchemaProperty): CustomPropertyType {
  // currencyMeasure is intentionally omitted: there is no matching
  // CustomPropertyInputType, so schemaTypeToPropTypes would collapse any
  // non-Unknown propType to [Unknown, Unknown] anyway.
  if (prop.enum) {
    if (prop.type === 'string') {
      return CustomPropertyType.SingleEnumValue;
    }
  } else if (prop['x-query']) {
    switch (prop.type) {
      case 'lengthMeasure':
      case 'areaMeasure':
      case 'volumeMeasure':
      case 'massMeasure':
      case 'angleMeasure':
        return CustomPropertyType.AutoCalculate;
      default:
        // Open schema `type` strings — unrecognized shapes fall through.
        break;
    }
  } else {
    switch (prop.type) {
      case 'string':
      case 'integer':
      case 'number':
      case 'lengthMeasure':
      case 'areaMeasure':
      case 'volumeMeasure':
      case 'massMeasure':
      case 'angleMeasure':
        return CustomPropertyType.SingleValue;
      case 'boolean':
        return CustomPropertyType.Logical;
      case 'array':
        return CustomPropertyType.ListValue;
      default:
        // Open schema `type` strings — unrecognized shapes fall through.
        break;
    }
  }

  return CustomPropertyType.Unknown;
}

/**
 * Classifies PropInputType from open OpenAPI `type`/`format` strings.
 * `default: break` → Unknown is intentional: these are not closed unions, so
 * UnreachableCaseError does not apply — throwing would crash on unknown schema.
 */
function schemaTypeToPropInputType(
  prop: PsetSchemaProperty,
  required: boolean
): CustomPropertyInputType {
  switch (prop.type) {
    case 'string':
      switch (prop.format) {
        case undefined:
          return CustomPropertyInputType.Text;
        case 'date-time':
          return CustomPropertyInputType.DateTime;
        case 'date':
          return CustomPropertyInputType.Date;
        case 'duration':
          return CustomPropertyInputType.Duration;
        case 'uri':
          return CustomPropertyInputType.URI;
        case 'query':
          return CustomPropertyInputType.Query;
        default:
          // Open schema `format` strings — unrecognized shapes fall through.
          break;
      }
      break;
    case 'array':
      return prop.items
        ? schemaTypeToPropInputType(prop.items, required)
        : CustomPropertyInputType.Unknown;
    case 'integer':
      return CustomPropertyInputType.NumericWhole;
    case 'number':
      return CustomPropertyInputType.NumericDecimal;
    case 'boolean':
      return required
        ? CustomPropertyInputType.Boolean
        : CustomPropertyInputType.Logical;
    case 'lengthMeasure':
      return CustomPropertyInputType.LengthMeasure;
    case 'areaMeasure':
      return CustomPropertyInputType.AreaMeasure;
    case 'volumeMeasure':
      return CustomPropertyInputType.VolumeMeasure;
    case 'massMeasure':
      return CustomPropertyInputType.MassMeasure;
    case 'angleMeasure':
      return CustomPropertyInputType.AngleMeasure;
    default:
      // Open schema `type` strings — unrecognized shapes fall through.
      break;
  }

  return CustomPropertyInputType.Unknown;
}

function makeTextProperty(
  id: string,
  value: unknown
): CustomPsetInstanceProperty {
  return {
    id,
    value: toPropertyValue(value),
    propType: CustomPropertyType.SingleValue,
    inputType: CustomPropertyInputType.Text,
  };
}

function getPropertyLabel(
  definition: DefinitionResponse,
  propId: string
): string {
  return getI18n(definition)?.props?.[propId] ?? propId;
}

function getI18n(
  definition: DefinitionResponse
): { name?: string; props?: Record<string, string> } | undefined {
  return definition.i18n?.['en-US'] as
    | { name?: string; props?: Record<string, string> }
    | undefined;
}

function asSchemaProperty(prop: unknown): PsetSchemaProperty {
  return typeof prop === 'object' && prop !== null
    ? (prop as PsetSchemaProperty)
    : {};
}

function getEnumOptions(prop: PsetSchemaProperty): readonly string[] {
  return (prop.enum ?? []).map((value) => String(value));
}

function isPropertyRequired(
  definition: DefinitionResponse,
  propId: string,
  prop: PsetSchemaProperty
): boolean {
  const schemaRequired = (definition.schema as PsetSchemaWithRequired).required;
  return prop.required === true || schemaRequired?.includes(propId) === true;
}

function toPropertyValue(value: unknown): CustomPsetPropertyValue {
  if (value === undefined || value === null) return value;

  if (Array.isArray(value)) {
    return value.map((item) => toScalarValue(item));
  }

  return toScalarValue(value);
}

function toScalarValue(value: unknown): CustomPsetScalarValue {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'bigint' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (isHighLowValue(value)) return value;

  return JSON.stringify(value);
}

function isHighLowValue(
  value: unknown
): value is { high: number; low: number } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'high' in value &&
    'low' in value &&
    typeof value.high === 'number' &&
    typeof value.low === 'number'
  );
}
